import useOrderFlow from '../../../../hooks/order/useOrderFlow';
import useOrderPreparationTime from '../../../../hooks/order/useOrderPreparationTime';
import useAppMethods from '../../../../hooks/utility/useAppMethods';
import { ORDER_METHODS, REDUX_STORE_SLICES } from '../../../../services/exports/Constants';
import { isOnSiteMethod } from '../../../../utils/on-site';
import useOrderMethodsStatus from '../../../../hooks/availability/useOrderMethodsStatus';
import moment from 'moment';
import useCompanyLocation from '../../../../hooks/location/useCompanyLocation';
import useStore from '../../../../hooks/store/useStore';
import useTheme from '../../../../hooks/ui/useTheme';
import AddressHelpers from '../../../../services/helpers/AddressHelpers';
import HelperMethods from '../../../../services/exports/HelperMethods';
import { useTranslation } from 'react-i18next';
import ClockIcon from '../../../../assets/icons/order-methods/ClockIcon';
import OrderMethodsTabBar from '../../../../components-library/navigation/OrderMethodsTabBar';
import LocationIcon from '../../../../assets/icons/LocationIcon';
import BackIcon from '../../../../assets/logo/BackIcon';
import classnames from 'classnames';
import useScreenType from '../../../../hooks/utility/useScreenType';
import { useRef } from 'react';
import { Utensils } from 'lucide-react';
import useTableOrder from '../../../../hooks/order/useTableOrder';
import useTableOrderStatus from '../../../../hooks/order/useTableOrderStatus';
import TableOrderLabel from '../../../../components-library/order-type/TableOrderLabel';
import TableName from '../../../../components-library/order-type/TableName';
import TableAvailabilityNote from '../../../../components-library/order-type/TableAvailabilityNote';

interface Props {
  className?: string;
}

const OrderMethod = ({ className }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Storefront:Components:Orders:OrderMethod' });
  const { t: tTable } = useTranslation(null, { keyPrefix: 'Components:OrderType:TableOrdering' });
  const { isAtTable, table } = useTableOrder();
  const tableStatus = useTableOrderStatus();
  const toggleRef = useRef<HTMLDivElement>(null);

  const { localData } = useOrderFlow();
  const { isDesktop } = useScreenType();
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const getPreparationTime = useOrderPreparationTime(company);
  const { formatDateTime, createScheduledOrderSlot, toggleOrderMethodModal } = useAppMethods();
  const { isOrderMethodCurrentlyOpen, isOrderMethodOpenForFutureOrders } = useOrderMethodsStatus();
  const theme = useTheme();
  const location = useCompanyLocation({
    datetime: localData.scheduled_for === null ? null : localData.scheduled_for,
  });

  const openOrderMethodModal = () => toggleOrderMethodModal({ openModal: true, orderMethod: localData.method });

  const address = (() => {
    if (localData.method === ORDER_METHODS.DELIVERY) {
      return AddressHelpers.format({
        street_name: localData?.delivery_street_name,
        city: localData?.delivery_city,
        zip_code: localData?.delivery_zip_code,
        format: company?.region?.address_format,
      });
    }

    return AddressHelpers.format({
      street_name: location?.address,
      city: location?.city,
      zip_code: location?.zip_code,
      format: company?.region?.address_format,
    });
  })();

  const preparationTime = (() => {
    const method = localData.method;
    const isMethodAvailable = isOrderMethodCurrentlyOpen(method) || isOrderMethodOpenForFutureOrders(method);

    if (!isMethodAvailable) return t('unavailable');

    if (localData.scheduled_for) {
      if (localData.scheduled_for === null) return t('asap');

      return moment(localData.scheduled_for).isSame(moment(), 'day')
        ? createScheduledOrderSlot(localData.scheduled_for)
        : formatDateTime(localData.scheduled_for, { format: 'dd, DD MMM HH:mm' });
    }

    return t('ready_in_time', {
      time: HelperMethods.formatTimeInterval(getPreparationTime(method)),
    });
  })();

  // Back in the toggle after "Pickup or delivery", for keyboard and screen-reader users: the tab the
  // cart landed in (the bar's button comes after the tabs and would open the modal).
  const focusToggle = () =>
    setTimeout(() => toggleRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus(), 0);

  if (isAtTable) {
    const tableTime = {
      open: tTable('served_in', { time: tableStatus.servedIn }),
      closed: tableStatus.openingText,
      paused: tTable('not_taking_orders'),
    }[tableStatus.state];

    // Read-only: the table comes from its QR code, so the bar is text, not a button.
    return (
      <div className={classnames(container, className)}>
        <div className="flex flex-col sm:flex-row items-stretch gap-sm lg:gap-md">
          <TableOrderLabel className={classnames({ 'w-full': !isDesktop })} onLeave={focusToggle} />
          <div className={classnames(tableBar, { 'w-full': !isDesktop })} data-test="table-order-bar">
            <Utensils aria-hidden="true" className="w-4 h-4 shrink-0" color={theme.color.brand.text.secondary} />
            <TableName name={table.name} className="text-sm" />
            <span className="text-brand-border">|</span>
            <ClockIcon className="w-3.5 h-3.5 shrink-0" color={theme.color.brand.text.secondary} />
            <span className="whitespace-nowrap text-xs">{tableTime}</span>
          </div>
        </div>
        <TableAvailabilityNote className="mt-sm" />
        {tableStatus.state === 'open' && (
          <p className={notYourTable} data-test="not-your-table">
            {tTable('not_your_table')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={classnames(container, className)}>
      <div className="flex flex-col sm:flex-row items-stretch gap-sm lg:gap-md" ref={toggleRef}>
        <OrderMethodsTabBar
          orderMethod={localData?.method}
          className={classnames(tabBarStyle, { 'w-full': !isDesktop })}
          tabBarItemClassName={tabBarItemStyle}
          tabBarItemActiveClassName={classnames(tabBarItemActiveStyle, { '!p-none': !isDesktop })}
        />
        <button
          className={classnames(locationTimeButton, { 'w-full': !isDesktop })}
          onClick={openOrderMethodModal}
          data-test="order-method-bar"
        >
          <LocationIcon className="w-4 h-4 shrink-0" color={theme.color.brand.text.secondary} />
          <span className="truncate text-sm">
            {/* An on-site cart whose table ran out reads as pickup until the effect moves it there. */}
            {t(`locations.${isOnSiteMethod(localData.method) ? ORDER_METHODS.PICKUP : localData.method}`, { address })}
          </span>
          <span className="text-brand-border">|</span>
          <ClockIcon className="w-3.5 h-3.5 shrink-0" color={theme.color.brand.text.secondary} />
          <span className="whitespace-nowrap text-xs">{preparationTime}</span>
          <BackIcon className="w-3 h-3 rotate-90 shrink-0" type={2} color={theme.color.brand.text.secondary} />
        </button>
      </div>
      <TableAvailabilityNote className="mt-sm" />
    </div>
  );
};

export default OrderMethod;

const container = 'w-full';
const tabBarStyle = '!rounded-lg !p-none h-[40px] lg:h-auto bg-gray-600 dark:!bg-brand-card';
const tabBarItemStyle = '!h-[40px] lg:!h-auto';
const tabBarItemActiveStyle =
  '!rounded-lg !h-[40px] lg:!h-auto !border shadow-section dark:shadow-none !border-gray-1000 dark:!border-brand-border';
const tableBar =
  'flex items-center gap-2 border border-brand-border rounded-lg px-sm h-[40px] lg:h-auto lg:py-sm lg:self-stretch text-sm text-muted-foreground min-w-0';
const notYourTable = 'mt-xs mb-0 text-xs text-muted-foreground';
const locationTimeButton =
  'flex items-center gap-2 border border-brand-border rounded-lg px-sm h-[40px] lg:h-auto lg:py-sm lg:self-stretch text-sm text-muted-foreground hover:text-brand-text-primary transition-colors';
